/**
 * Section Animations - Scroll Reveal & Counter System
 * Shared across all custom sections for consistent, performant animations.
 *
 * Usage:
 *   <div data-reveal="fade-up">...</div>
 *   <div data-reveal="fade-up" data-reveal-delay="150">...</div>
 *   <span data-counter>5000+</span>
 *
 * Reveal types: fade-up, fade-down, fade-left, fade-right, scale-up, fade-in
 */
(function () {
  if (window.__sectionAnimations) return;
  window.__sectionAnimations = true;

  /* ── Base reveal styles (injected once) ── */
  const css = document.createElement('style');
  css.id = 'section-animations-css';
  css.textContent = `
    [data-reveal] {
      opacity: 0;
      transition:
        opacity 0.3s ease-out,
        transform 0.3s ease-out;
    }
    [data-reveal="fade-up"]    { transform: translateY(24px); }
    [data-reveal="fade-down"]  { transform: translateY(-24px); }
    [data-reveal="fade-left"]  { transform: translateX(24px); }
    [data-reveal="fade-right"] { transform: translateX(-24px); }
    [data-reveal="scale-up"]   { transform: scale(0.95); }
    [data-reveal="fade-in"]    { transform: none; }

    [data-reveal].is-revealed {
      opacity: 1;
      transform: none;
    }

    /* Respect reduced motion */
    @media (prefers-reduced-motion: reduce) {
      [data-reveal] {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(css);

  /* ── Scroll reveal observer ── */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const delay = parseInt(el.dataset.revealDelay || '0', 10);
        if (delay > 0) {
          setTimeout(() => el.classList.add('is-revealed'), delay);
        } else {
          el.classList.add('is-revealed');
        }
        revealObserver.unobserve(el);
      });
    },
    { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
  );

  /* ── Counter animation ── */
  function animateCounter(el) {
    const text = el.textContent.trim();
    const match = text.match(/([\d.,]+)/);
    if (!match) return;

    const raw = match[1];
    const prefix = text.slice(0, text.indexOf(raw));
    const suffix = text.slice(text.indexOf(raw) + raw.length);
    const cleaned = raw.replace(/\./g, '').replace(',', '.');
    const target = parseFloat(cleaned);
    if (isNaN(target)) return;

    const hasDecimal = raw.includes(',') && raw.split(',')[1]?.length <= 2;
    const decimals = hasDecimal ? raw.split(',')[1].length : 0;
    const useThousands = raw.includes('.');
    const duration = 2200;
    const start = performance.now();

    el.style.fontVariantNumeric = 'tabular-nums';

    function format(n) {
      let str;
      if (decimals > 0) {
        str = n.toFixed(decimals).replace('.', ',');
      } else {
        str = Math.floor(n).toString();
      }
      if (useThousands && str.length > 3) {
        const parts = str.split(',');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        str = parts.join(',');
      }
      return prefix + str + suffix;
    }

    function tick(now) {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 4);
      el.textContent = format(ease * target);
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = text;
    }

    requestAnimationFrame(tick);
  }

  const counterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          counterObserver.unobserve(el);
          return;
        }
        const delay = parseInt(el.dataset.counterDelay || '0', 10);
        if (delay > 0) {
          setTimeout(() => animateCounter(el), delay);
        } else {
          animateCounter(el);
        }
        counterObserver.unobserve(el);
      });
    },
    { threshold: 0.3 }
  );

  /* ── Initialization ── */
  function init() {
    document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
      revealObserver.observe(el);
    });
    document.querySelectorAll('[data-counter]:not([data-counter-done])').forEach((el) => {
      el.setAttribute('data-counter-done', '');
      counterObserver.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init on Shopify theme editor events */
  document.addEventListener('shopify:section:load', () => {
    requestAnimationFrame(init);
  });

  /* Catch dynamically added elements */
  const mo = new MutationObserver((mutations) => {
    let found = false;
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (n.nodeType === 1 && (n.hasAttribute?.('data-reveal') || n.hasAttribute?.('data-counter') || n.querySelector?.('[data-reveal], [data-counter]'))) {
          found = true;
          break;
        }
      }
      if (found) break;
    }
    if (found) requestAnimationFrame(init);
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
